/**
 * WelcomeSplash — V3 Day 2.5 (2026-05-08)
 *
 * Full-screen entry that meets the prospect with the cinematic hero
 * shot of the tower before they touch the configurator. Two CTAs:
 *
 *   ▶ شاهد المشروع   → opens ProjectShowcase modal (auto-tour)
 *   → ابدأ التخصيص    → navigates to /welcome (Step1 — collect client info)
 *
 * On first close (any CTA), we set a sessionStorage flag so a return
 * visit during the same browser session lands directly on /welcome.
 * The user can still come back to the splash via the small "back home"
 * link added to Step1Welcome.
 *
 * Hero image: building/exterior-night-1.jpg (the same hero we picked
 * for ProjectShowcase). Lazy-loaded with a blur-up placeholder so the
 * splash renders instantly on slow connections.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Globe, PlayCircle, ArrowRight } from 'lucide-react';
import manifest from '@data/assets-manifest.json';
import ProjectShowcase from '@components/ProjectShowcase';
import { useT } from '@lib/i18n';
import { useSessionStore } from '@stores/sessionStore';
import type { Language } from '@lib/types';

const SPLASH_VERSION = 'v2-20260508';
const HERO_KEY = 'exterior-night-1';

function withVersion(url: string): string {
  return url.includes('?')
    ? `${url}&v=${SPLASH_VERSION}`
    : `${url}?v=${SPLASH_VERSION}`;
}

export default function WelcomeSplash() {
  const t = useT();
  const navigate = useNavigate();
  const language = useSessionStore((s) => s.language);
  const isAr = language === 'ar';
  const dir: 'ltr' | 'rtl' = isAr ? 'rtl' : 'ltr';

  const heroUrl = useMemo(() => {
    const url = (manifest as { building: Record<string, string> }).building[HERO_KEY];
    return url ? withVersion(url) : '';
  }, []);

  const [imgLoaded, setImgLoaded] = useState(false);
  const [showShowcase, setShowShowcase] = useState(false);

  // ─── Subtle parallax (max ±15px) ──────────────────────────────
  const heroRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const el = heroRef.current;
    if (!el) return;
    let raf = 0;
    const onMove = (e: MouseEvent) => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const w = window.innerWidth;
        const h = window.innerHeight;
        const x = (e.clientX / w - 0.5) * 30; // -15 .. +15
        const y = (e.clientY / h - 0.5) * 30;
        el.style.transform = `translate3d(${-x}px, ${-y}px, 0) scale(1.05)`;
      });
    };
    window.addEventListener('mousemove', onMove);
    return () => {
      window.removeEventListener('mousemove', onMove);
      cancelAnimationFrame(raf);
    };
  }, []);

  // ─── Mark splash as seen on either CTA ───────────────────────
  const markSeen = () => {
    try {
      sessionStorage.setItem('key2:hasSeenSplash', '1');
    } catch {
      /* private mode etc — ignore */
    }
  };

  const handleStart = () => {
    markSeen();
    navigate('/welcome');
  };

  const handleWatch = () => {
    markSeen();
    setShowShowcase(true);
  };

  const handleLanguage = (next: Language) => {
    if (next === language) return;
    // Match Step1Welcome's pattern — language lives in the persisted
    // session store and is set directly via setState (no thunk).
    useSessionStore.setState({ language: next });
  };

  return (
    <div
      dir={dir}
      className="relative w-full h-screen overflow-hidden bg-navy text-white"
      role="region"
      aria-label={t('showcase.title')}
    >
      {/* ── Hero image (parallax + blur-up) ─────────────────── */}
      <div
        ref={heroRef}
        className="absolute inset-0 will-change-transform transition-transform duration-200 ease-out"
        style={{ transform: 'scale(1.05)' }}
      >
        {/* Low-cost solid colour fallback while heavy image loads */}
        <div className="absolute inset-0 bg-gradient-to-br from-navy via-navy-900 to-black" />
        {heroUrl && (
          <img
            src={heroUrl}
            alt=""
            crossOrigin="anonymous"
            loading="eager"
            decoding="async"
            onLoad={() => setImgLoaded(true)}
            className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-700 ${
              imgLoaded ? 'opacity-100' : 'opacity-0 blur-md'
            }`}
            draggable={false}
          />
        )}
      </div>

      {/* ── Dark gradient overlay (heavier toward bottom) ─── */}
      <div
        className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/40 to-black/85"
        aria-hidden
      />

      {/* ── Top bar — language toggle on the start side ───── */}
      <div className="absolute top-3 start-3 z-20">
        <LangToggle current={language} onChange={handleLanguage} />
      </div>

      {/* ── Centered content ─────────────────────────────── */}
      <div className="relative z-10 h-full w-full flex flex-col items-center justify-center px-6 text-center">
        {/* Brand mark — text only (no logo file shipped yet) */}
        <div className="mb-3">
          <p className="text-xs md:text-sm tracking-[0.3em] text-gold/90 uppercase">
            {t('welcome.brand')}
          </p>
        </div>
        <h1 className="font-extrabold text-5xl md:text-7xl leading-none mb-4 select-none">
          <span className="bg-gradient-to-r from-gold-300 via-gold to-gold-300 bg-clip-text text-transparent">
            KEY2
          </span>
        </h1>
        <p className="max-w-xl text-base md:text-2xl font-light text-white/90 mb-10 leading-relaxed">
          {t('welcome.tagline')}
        </p>

        <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 w-full max-w-md">
          <button
            type="button"
            onClick={handleWatch}
            className="flex-1 inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full
                       font-bold text-navy bg-gradient-to-r from-yellow-500 to-yellow-600
                       hover:from-yellow-400 hover:to-yellow-500 shadow-lg transition-all
                       focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold/60"
          >
            <PlayCircle className="w-5 h-5" />
            <span>{t('welcome.watchProject')}</span>
          </button>

          <button
            type="button"
            onClick={handleStart}
            className="flex-1 inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full
                       font-bold text-gold border-2 border-gold/80 bg-white/5 backdrop-blur-sm
                       hover:bg-gold hover:text-navy transition-all
                       focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold/60"
          >
            <span>{t('welcome.startConfig')}</span>
            <ArrowRight className={`w-5 h-5 ${isAr ? 'rotate-180' : ''}`} />
          </button>
        </div>
      </div>

      {/* ── Footer ──────────────────────────────────────────── */}
      <div className="absolute inset-x-0 bottom-3 z-10 flex justify-center text-[11px] tracking-wide text-white/55">
        <span>{t('welcome.brand')}</span>
        <span className="mx-2">·</span>
        <span className="num-en">KEY2</span>
      </div>

      {/* ── Showcase modal (re-uses Day 2 component) ──────── */}
      <ProjectShowcase
        open={showShowcase}
        onClose={() => setShowShowcase(false)}
      />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// Lang toggle — small, splash-flavoured (semi-transparent)
// ─────────────────────────────────────────────────────────────────

function LangToggle({
  current,
  onChange,
}: {
  current: Language;
  onChange: (l: Language) => void;
}) {
  return (
    <div
      className="inline-flex items-center gap-1 rounded-full bg-black/45 backdrop-blur-sm border border-white/15 p-1"
      role="group"
      aria-label="Language"
    >
      <Globe className="w-3.5 h-3.5 text-white/70 mx-1.5" aria-hidden />
      <LangBtn label="عربي" active={current === 'ar'} onClick={() => onChange('ar')} />
      <LangBtn label="EN"   active={current === 'en'} onClick={() => onChange('en')} />
    </div>
  );
}

function LangBtn({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`px-3 py-1 text-xs font-semibold rounded-full transition-colors ${
        active
          ? 'bg-gold text-navy'
          : 'text-white/80 hover:text-white hover:bg-white/10'
      }`}
    >
      {label}
    </button>
  );
}
